import CollaborationLayout from 'Layouts/collaboration/CollaborationLayout'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Col, DropdownItem, DropdownMenu, DropdownToggle, Modal, ModalBody, Row, Spinner, UncontrolledDropdown } from 'reactstrap'
import userDummayImage from "../../assets/images/users/user-dummy-img.jpg";
import SimpleBar from 'simplebar-react';
import { AppDispatch, RootState, store } from 'store';
import { useDispatch, useSelector } from 'react-redux';
import { Gig, Milestone, MilestoneEvent, MilestoneStep } from 'types/collaboration';
import { fetchAllCollaborationsThunk, fetchCollaborationChatRoom, fetchCollaborationMilestoneData } from 'slices/collaborations/thunk';
import { fetchPermalinkFile } from 'slices/collaborations/files/reducer';
import { destroyCollabRemovalRequest } from 'services/api/collaboration';
import GigSelectionModal from 'Components/collaboration/milestone/GigSelection';
import FormBuilderModal from 'Components/collaboration/milestone/FormBuilder';
import CurrentStep from 'Components/collaboration/milestone/step/CurrentStep';
import Timeline from 'Components/collaboration/milestone/tabs/Timeline';
import Specifications from 'Components/collaboration/milestone/tabs/Specifications';
import CollaborationContent from 'Components/collaboration/milestone/tabs/CollaborationContent';
import FeedbackModal from 'Components/collaboration/milestone/components/Feedback';
import DeadlineCalendarModal from 'Components/collaboration/milestone/components/DeadlineCalendar';
import { getMediaTypeFromURL, getErrorMessage } from 'utils/helpers';
import "./collaboration.scss";
import InfluencerPostModal from 'Components/influencer/InfluencerPostModal';
import { useIdleTimer } from 'react-idle-timer';
import Loader from 'Components/Common/Loader';
import { fetchLegacyCollaborationThunk } from 'slices/legacy-collaborations/thunk';
import { resetTasksSummary } from 'slices/collaborations/reducer';
import SocialPostSelectionModal from 'Components/collaboration/milestone/SocialPostSelection';
import AddressSelectionModal from 'Components/collaboration/milestone/AddressSelection';
import GigAdditionModal from 'Components/collaboration/milestone/GigAddition';
import { useMediaQuery } from 'hooks/useMediaQuery';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import Spinners from 'Components/Common/Spinner';
import CollaborationChat from 'Components/collaboration/CollaborationChat';
import RequestCollabDeletionModal from 'Components/collaboration/RequestCollabDeletionModal';
import { toggleAddContactMobileModal, toggleUpdateCreatorProfileModal } from 'slices/ui/reducer';

const PROFILE_COMPLETE = 100

const TaskMilestone = () => {

    const { campaignId, collaborationId, milestoneId } = useParams();
    const [searchParams] = useSearchParams();
    const filtersQuery = searchParams.get('filters');

    const dispatch: AppDispatch = useDispatch();
    const navigate = useNavigate();
    const { isDesktop } = useMediaQuery();

    const tabsRef = useRef<HTMLDivElement | null>(null);

    // Redux Data //
    const influencerPostModalVisibile = useSelector((state: RootState) => state.ui.influencerPostModal);
    const milestoneData = useSelector((state: RootState) => state.collaborations.milestones?.[campaignId!]?.[collaborationId!]?.[milestoneId!]?.milestone);
    const activeMilestone = milestoneData?.milestone ?? null;
    const gigs = milestoneData?.gigs ?? [];
    const selectedHubId = useSelector((state: RootState) => state.auth.selectedHubId);

    const collaborationData = useSelector((state: RootState) => state.collaborations.items?.[campaignId!]?.[collaborationId!]);

    const legacyCollabData = useSelector((state: RootState) => state.legacyCollaborations.items[campaignId!]?.[collaborationId!]);
    const legacyCollabLoading = useSelector((state: RootState) => state.legacyCollaborations.itemLoading);
    const collaboration = legacyCollabData?.collaboration ?? null;
    const milestones = collaborationData?.milestones ?? [];
    const campaign = collaborationData?.campaign;
    const influencer = collaboration?.influencer ?? null;
    const needsCreatorMobile = !influencer;
    const needsProfileCompletion =
        !!influencer && (influencer.profile_completion ?? 0) !== PROFILE_COMPLETE;
    const isCreatorSetupIncomplete = needsCreatorMobile || needsProfileCompletion;

    const addContactMobileModal = useSelector((state: RootState) => state.ui.addContactMobileModal);
    const updateCreatorProfileModal = useSelector((state: RootState) => state.ui.updateCreatorProfileModal);
    const posts = milestoneId ? (milestoneData?.posts ?? []) : (collaboration?.posts ?? []);
    const acknowledged = activeMilestone?.acknowledged_at ?? false;
    const [currentTasks, setCurrentTasks] = useState<MilestoneStep[]>([]);
    const [events, setEvents] = useState<MilestoneEvent[]>([]);
    const chatRoom = useSelector((state: RootState) => state.collaborations.chatRooms[campaignId!]?.[collaborationId!]?.room);
    const chatRoomId = legacyCollabData?.chat?.uuid ?? (chatRoom?.uuid ?? null);
    const collabLoading = useSelector((state: RootState) => state.collaborations.itemLoading);
    const creatorMobile = collaboration?.influencer?.mobile ?? null;
    const collabFilters = useSelector((state: RootState) => state.collaborations.filters);
    const hasApprovedMedia = useMemo(() => {
        const collabPosts = collaboration?.posts ?? [];
        const milestonePosts = milestoneData?.posts ?? [];
        return [...collabPosts, ...milestonePosts].some((post) => post.status === 'approved');
    }, [collaboration?.posts, milestoneData?.posts]);
    const deletionRequest = collaboration?.deletion_request ?? null;
    const canRequestCollabDeletion = !legacyCollabLoading && !!collaboration && !isCreatorSetupIncomplete && !hasApprovedMedia && !deletionRequest;
    const canRevertCollabDeletion = !legacyCollabLoading && !!collaboration && !isCreatorSetupIncomplete && !!deletionRequest && false;

    const showKebabMenu = useMemo(() => {
        return canRequestCollabDeletion || canRevertCollabDeletion;
    }, [canRequestCollabDeletion, canRevertCollabDeletion]);

    // Redux Data //

    // Local States //
    const [openedStep, setOpenedStep] = useState<string | null>(null);
    const [selectedMedia, setSelectedMedia] = useState<any>(null);
    const [activeTab, setActiveTab] = useState('timeline');
    const [isDownloading, setIsDownloading] = useState(false);
    const [showDisabledTooltip, setShowDisabledTooltip] = useState(false);
    const [feedback, setFeedback] = useState('');
    const [deadlineCalendarStep, setDeadlineCalendarStep] = useState<MilestoneStep | null>(null);
    const [loading, setLoading] = useState(false);
    const [chatSectionVisibility, setChatSectionVisibility] = useState(false);
    const [creatingChatRoom, setCreatingChatRoom] = useState(false);
    const [deletionRequestModalOpen, setDeletionRequestModalOpen] = useState(false);
    const isMobileChatOpen = !isDesktop && chatSectionVisibility;
    const showCollabSection = isDesktop || !chatSectionVisibility;
    const showChatSection = isDesktop || chatSectionVisibility;
    // Local States //

    // Functions //    

    const getCreatorName = () =>
        collaborationData?.influencer?.name ??
        collaborationData?.social_account?.name ??
        collaborationData?.social_account?.handle ??
        collaboration?.influencer?.name ??
        collaboration?.social_account?.name ??
        collaboration?.social_account?.handle

    const handleAddCreatorMobile = () => {
        dispatch(
            toggleAddContactMobileModal({
                socialAccountId: collaboration?.social_account?.uuid ?? '',
                creatorName: getCreatorName(),
            })
        )
    }

    const handleCompleteProfile = () => {
        if (!influencer) return
        dispatch(
            toggleUpdateCreatorProfileModal({
                influencerId: influencer.uuid,
                creatorName: getCreatorName(),
            })
        )
    }

    const createChatRoom = async () => {
        if (creatingChatRoom || !campaignId || !collaborationId) return;
        setCreatingChatRoom(true);
        try {
            await dispatch(fetchCollaborationChatRoom({ campaignId, collaborationId }))
        } catch (error: any) {
            toast.error("Failed to open chat room, Please try again later!");
        } finally {
            setCreatingChatRoom(false);
        }
    }

    useEffect(() => {
        setChatSectionVisibility(false);
        setDeletionRequestModalOpen(false);
    }, [collaborationId, campaignId]);


    const getMilestoneName = (milestone: Milestone) => {
        return `${milestones.length > 1 ? `#${milestone?.sort_order} ` : ''}${milestone?.gig_type?.name}`
    }

    const fetchCollaborationMilestones = useCallback(async (force = false) => {
        if (campaignId && milestoneId && collaborationId)
            await dispatch(fetchCollaborationMilestoneData({ campaignId, milestoneId, collaborationId, force }))
    }, [dispatch, campaignId, milestoneId, collaborationId])

    const fetchCollaborationData = useCallback(async (force = false) => {
        if (campaignId && collaborationId)
            await dispatch(fetchLegacyCollaborationThunk({ campaignId, collaborationId, force }))
    }, [dispatch, campaignId, collaborationId])

    const onPlayButtonClick = async (gig: Gig) => {

        if (!campaignId || !selectedHubId || !collaborationId) return;
        try {
            const result = await dispatch(
                fetchPermalinkFile({ campaignId, hubId: selectedHubId, collaborationId, permalink: gig.properties.permalink })
            ).unwrap();

            setSelectedMedia({ ...result.file, gig });
        } catch (error) {
            window.open(gig.properties.permalink, '_blank');
            console.log(error);
        }
    }

    const handleDownload = async (gig: Gig) => {
        if (!campaignId) return;

        try {
            // setIsDownloading(true);
            // const blob = await fetchCollaborationPermalinkFile(
            //     campaignId,
            //     gig.properties.permalink,
            //     'download'
            // );

            // const blobUrl = window.URL.createObjectURL(blob);
            // const link = document.createElement('a');
            // link.href = blobUrl;
            // link.setAttribute('download', `${gig.name}.mp4`);
            // document.body.appendChild(link);
            // link.click();
            // link.remove();
            // window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error("Download failed:", error);
        } finally {
            setIsDownloading(false);
        }
    };
    // Functions //

    // Memoized Data //
    const tabs = useMemo(() => {
        return [
            // { key: 'current', label: 'Current Task' },
            { key: 'timeline', label: 'Timeline' },
            ...(milestoneId ? [{ key: 'specs', label: 'Brief' }] : []),
            { key: 'content', label: 'Content' }
        ]
    }, [acknowledged, milestoneId])

    const getCurrentStepsComponents = useMemo(() => {
        let components: React.ReactNode[] = [];
        if (!activeMilestone) return components;

        const onCloseHandler = (refetch?: boolean) => {
            setOpenedStep(null)
            if (refetch) {
                fetchCollaborationMilestones(true);
                dispatch(resetTasksSummary())
            }
        }

        for (let task of currentTasks) {
            if (task.gig_step.slug === 'gig_selection') {
                components.push(
                    <GigSelectionModal
                        isActive={openedStep === 'gig_selection'}
                        onClose={onCloseHandler}
                        activeMilestone={activeMilestone}
                        gigs={gigs}
                        onPlayButtonClick={onPlayButtonClick}
                        handleDownload={handleDownload}
                        step={task}
                    />
                )
            } else if (task.gig_step.slug === 'gig_addition') {
                components.push(
                    <GigAdditionModal
                        isActive={openedStep === 'gig_addition'}
                        onClose={onCloseHandler}
                        activeMilestone={activeMilestone}
                        gigs={gigs}
                        onPlayButtonClick={onPlayButtonClick}
                        handleDownload={handleDownload}
                        step={task}
                    />
                )
            } else if (task.gig_step.slug === 'address_submission') {
                components.push(
                    <AddressSelectionModal
                        isActive={openedStep === 'address_submission'}
                        onClose={onCloseHandler}
                        activeMilestone={activeMilestone}
                        step={task}
                    />
                )
            } else if (['post_selection', 'reel_selection', 'story_selection', 'video_selection'].includes(task.gig_step.slug)) {
                components.push(
                    <SocialPostSelectionModal
                        isActive={openedStep === task.gig_step.slug}
                        onClose={onCloseHandler}
                        activeMilestone={activeMilestone}
                        gigs={gigs}
                        setSelectedMedia={(post: any) => setSelectedMedia(post)}
                        step={task}
                    />
                )
            } else {
                components.push(
                    <FormBuilderModal
                        isActive={openedStep === task.gig_step.slug}
                        onClose={onCloseHandler}
                        title={task.gig_step.title ?? task.gig_step.name}
                        activeMilestone={activeMilestone}
                        step={task}
                    />
                )
            }
        }
        return components;
    }, [currentTasks, activeMilestone, gigs, openedStep])

    // Memoized Data //


    // Lifecycle Methods //

    useEffect(() => {
        if (!milestoneId) {
            setActiveTab('timeline');
        }
    }, [milestoneId])


    useEffect(() => {
        if (!milestoneId && campaignId && collaborationId && milestones.length > 0) {
            const _milestones = [...milestones].sort((a, b) => b.sort_order - a.sort_order);
            navigate(`/tasks/${campaignId}/collaboration/${collaborationId}/milestone/${_milestones[0].id}`);
        }
    }, [milestoneId, milestones, campaignId, collaborationId])

    useEffect(() => {
        if (!milestoneData && !milestoneId && collaboration) {
            const pendingPost = (collaboration?.posts ?? []).find((post) => post.status === 'pending');

            const allRejected = (collaboration?.posts ?? []).filter((post) => post.status === 'rejected').length === (collaboration?.posts ?? []).length

            const step: MilestoneStep = {
                sort_order: 1,
                is_locked: false,
                is_processed: true,
                processed: null,
                deliverables: {
                },
                can_process: false,
                gig_step: {
                    slug: 'ig_media_approval',
                    name: "Instagram Media Approval*",
                    title: (pendingPost || allRejected) ? "Influencer has uploaded live media" : "Instagram media has been approved",
                    description: "",
                    cta_text: "",
                    revision_required_title: "",
                    processed_title: "",
                    object_name: "",
                    properties: [],
                    attributes: {},
                    require_deadline: false,
                },
                deadline: null,
                complete: !(pendingPost || allRejected)
            }
            setCurrentTasks([step]);
            setEvents([]);
            return;
        }
        if (Array.isArray(milestoneData?.active_steps)) {
            let steps = [...milestoneData?.active_steps].sort((a, b) => a.sort_order - b.sort_order);
            setCurrentTasks(steps);
        }
        if (Array.isArray(milestoneData?.events)) {
            let timelineEvents = [...milestoneData?.events].sort((a, b) => (new Date(a.created_at)).getTime() - (new Date(b.created_at)).getTime());
            setEvents(timelineEvents);
        }
    }, [milestoneData, milestoneId, collaboration])

    useEffect(() => {
        if (campaignId) {
            fetchCollaborationMilestones();
        }
    }, [campaignId, milestoneId, fetchCollaborationMilestones])

    useEffect(() => {
        if (campaignId && collaborationId) {
            fetchCollaborationData();
        }
    }, [campaignId, collaborationId, fetchCollaborationData])

    useEffect(() => {
        const hideDisabledTooltip = () => {
            setShowDisabledTooltip(false);
        }

        if (showDisabledTooltip) {
            setTimeout(() => {
                hideDisabledTooltip()
            }, 3000)
            document.addEventListener('scroll', hideDisabledTooltip);
        }

        return () => {
            document.removeEventListener('scroll', hideDisabledTooltip);
        }
    }, [showDisabledTooltip])

    // Lifecycle Methods //

    useIdleTimer({
        timeout: 1000 * 60 * 5, // 5 minutes
        onActive: () => {
            fetchCollaborationMilestones(true)
            fetchCollaborationData(true);
        },
        throttle: 1000,
    });

    const forceRefetch = async () => {
        setLoading(true);
        await fetchCollaborationMilestones(true);
        await fetchCollaborationData(true);
        setLoading(false);
    }

    const refreshCollabApis = () => {
        fetchCollaborationMilestones(true);
        fetchCollaborationData(true);
        dispatch(resetTasksSummary());
        dispatch(fetchAllCollaborationsThunk({ filters: { ...collabFilters, page: 1 }, force: true }));
    }


    useEffect(() => {
        setActiveTab('timeline');
    }, [collaborationId, campaignId, milestoneId])

    useEffect(() => {
        tabsRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [activeTab])

    const prevAddContactMobileModalRef = useRef(addContactMobileModal)
    useEffect(() => {
        if (prevAddContactMobileModalRef.current && !addContactMobileModal) {
            fetchCollaborationData(true)
        }
        prevAddContactMobileModalRef.current = addContactMobileModal
    }, [addContactMobileModal, fetchCollaborationData])

    const prevUpdateCreatorProfileModalRef = useRef(updateCreatorProfileModal)
    useEffect(() => {
        if (prevUpdateCreatorProfileModalRef.current && !updateCreatorProfileModal) {
            fetchCollaborationData(true)
        }
        prevUpdateCreatorProfileModalRef.current = updateCreatorProfileModal
    }, [updateCreatorProfileModal, fetchCollaborationData])

    const handleRevertCollabDeletion = async () => {
        if (!campaignId || !collaborationId || !selectedHubId || !deletionRequest?.id) return;

        const result = await Swal.fire({
            title: 'Revert removal request?',
            text: 'This will cancel the collab deletion request.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, revert',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#fa896b',
        });

        if (!result.isConfirmed) return;

        try {
            await destroyCollabRemovalRequest(selectedHubId, campaignId, collaborationId, deletionRequest.id);
            toast.success('Collab deletion request reverted.');
            refreshCollabApis();
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to revert deletion request.'));
        }
    }

    if (loading) return <Loader />

    return (
        <CollaborationLayout>
            {
                !collaborationId &&
                <div className="d-flex h-100 w-100 justify-content-center align-items-center">
                    <div className="length-zero m-0">
                        <h3>No Task Selected!</h3>
                    </div>
                </div>
            }
            {
                collaborationId &&
                <div className="chat-content d-lg-flex">
                    <div className="w-100 overflow-hidden position-relative">
                        <div className="position-relative">
                            <div className="p-3 user-chat-topbar">
                                <div className="d-flex justify-content-between gap-2 align-items-center">
                                    <div className="d-flex align-items-center w-75">
                                        <div className="flex-shrink-0 d-block d-lg-none me-1">
                                            {isMobileChatOpen ? (
                                                <button
                                                    type="button"
                                                    className="unset-all user-chat-remove fs-18 p-1"
                                                    onClick={() => setChatSectionVisibility(false)}
                                                    aria-label="Back to collaboration"
                                                >
                                                    <i className="ri-arrow-left-s-line align-bottom"></i>
                                                </button>
                                            ) : (
                                                <Link to={{ pathname: "/tasks", search: filtersQuery ? `?filters=${filtersQuery}` : '' }} className="user-chat-remove fs-18 p-1">
                                                    <i className="ri-arrow-left-s-line align-bottom"></i>
                                                </Link>
                                            )}
                                        </div>
                                        <div className="flex-grow-1 overflow-hidden">
                                            <div className="d-flex align-items-center">
                                                <div className="flex-shrink-0 chat-user-img online user-own-img align-self-center me-2 ms-0">
                                                    {collaborationData?.social_account?.profile_picture_url ? (
                                                        <img src={collaborationData.social_account?.profile_picture_url} className="rounded-circle avatar-xs" alt="" />
                                                    ) : (
                                                        <img src={userDummayImage} className="rounded-circle avatar-xs" alt="" />
                                                    )}
                                                </div>
                                                <div className="overflow-hidden">
                                                    <h5 className="text-truncate w-100 mb-0 fs-16">
                                                        <span
                                                        >
                                                            {collaborationData?.influencer?.name ?? collaborationData?.social_account?.name ?? collaborationData?.social_account?.handle}
                                                            {
                                                                collaboration?.language &&
                                                                ` [${collaboration.language.name}]`
                                                            }
                                                            {
                                                                creatorMobile &&
                                                                <a href={`https://wa.me/91${creatorMobile}`} target='_blank' rel='noopener noreferrer'>
                                                                    <button className='unset-all ms-2'>
                                                                        <i className="ri-whatsapp-line text-success "></i>
                                                                    </button>
                                                                </a>
                                                            }
                                                            <p className="text-muted fs-12 mb-0">{campaign?.name}</p>
                                                        </span>
                                                    </h5>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className='w-100 d-flex justify-content-end align-items-center gap-2'>
                                        {
                                            activeMilestone && milestones.length > 1 &&
                                            <UncontrolledDropdown className='milestone-dropdown'>
                                                <DropdownToggle tag={"button"} className='milestone-dropdown-btn btn btn-white d-flex justify-content-between align-items-center w-100'>
                                                    <span className='text-truncate milestone-name-dropdown-text'>
                                                        {getMilestoneName(activeMilestone)}
                                                    </span>
                                                    <i className='mdi mdi-chevron-down text-007AFF fs-5' />
                                                </DropdownToggle>
                                                <DropdownMenu className='mt-1 p-0 w-100'>
                                                    {
                                                        milestones.map((_milestone, i) => <DropdownItem onClick={() => navigate(`/tasks/${campaignId}/collaboration/${collaborationId}/milestone/${_milestone.id}${filtersQuery ? '?filters=' + filtersQuery : ''}`)} key={i}>{getMilestoneName(_milestone)}</DropdownItem>)
                                                    }
                                                </DropdownMenu>
                                            </UncontrolledDropdown>
                                        }
                                        {
                                            !isDesktop && !isCreatorSetupIncomplete &&
                                            <span
                                                onClick={() => setChatSectionVisibility(!chatSectionVisibility)}
                                                title='Chat'
                                                className='cursor-pointer d-flex align-items-center'
                                                role="button"
                                                aria-label={chatSectionVisibility ? 'Close chat' : 'Open chat'}
                                            >
                                                <i className={`fs-4 mdi text-primary ${chatSectionVisibility ? 'mdi-chat-processing' : 'mdi-chat-processing-outline'}`}></i>
                                            </span>
                                        }
                                        <span onClick={() => forceRefetch()} title='Refresh' className='cursor-pointer d-flex align-items-center'><i className='fs-4 bx bx-refresh text-primary' ></i></span>
                                        {
                                            showKebabMenu &&
                                            <UncontrolledDropdown>
                                                <DropdownToggle
                                                    tag="span"
                                                    title="More"
                                                    className="cursor-pointer d-flex align-items-center"
                                                    role="button"
                                                    aria-label="More actions"
                                                >
                                                    <i className="fs-4 mdi mdi-dots-vertical text-primary"></i>
                                                </DropdownToggle>
                                                <DropdownMenu end container="body" className="mt-1">
                                                    {
                                                        canRequestCollabDeletion &&
                                                        <DropdownItem onClick={() => setDeletionRequestModalOpen(true)}>
                                                            Request Removal
                                                        </DropdownItem>
                                                    }
                                                    {
                                                        canRevertCollabDeletion &&
                                                        <DropdownItem onClick={handleRevertCollabDeletion}>
                                                            Revert Removal Request
                                                        </DropdownItem>
                                                    }
                                                </DropdownMenu>
                                            </UncontrolledDropdown>
                                        }
                                    </div>
                                </div>
                            </div>

                            <RequestCollabDeletionModal
                                isActive={deletionRequestModalOpen}
                                onClose={() => setDeletionRequestModalOpen(false)}
                                onSuccess={refreshCollabApis}
                            />

                            <div className="position-relative" id="users-chat">
                                <div className={`chat-conversation simplebar-scrollable-y ${isDesktop ? 'd-flex' : ''}`} id="chat-conversation">
                                    {
                                        legacyCollabLoading ? <div className="d-flex h-100 w-100 justify-content-center align-items-center">
                                            <div className="length-zero m-0 d-flex flex-column justify-content-center align-items-center gap-2">
                                                <Spinner />
                                                <h3>Loading collaboration data...</h3>
                                            </div>
                                        </div> : (
                                            <>
                                                {(isCreatorSetupIncomplete) ? (
                                                    <div className="w-100 d-flex flex-column justify-content-center align-items-center p-4 text-center">
                                                        {needsProfileCompletion ? (
                                                            <>
                                                                <i className="ri-user-settings-line fs-1 text-muted mb-3" aria-hidden />
                                                                <h5 className="mb-2">Complete creator profile to start collaboration</h5>
                                                                <p className="text-muted mb-4 fs-14">
                                                                    Please complete the creator&apos;s profile to start collaboration.
                                                                </p>
                                                                <button
                                                                    type="button"
                                                                    className="theme-btn btn-primary"
                                                                    onClick={handleCompleteProfile}
                                                                >
                                                                    Complete Profile
                                                                </button>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="ri-phone-line fs-1 text-muted mb-3" aria-hidden />
                                                                <h5 className="mb-2">Add creator mobile to start collaboration</h5>
                                                                <p className="text-muted mb-4 fs-14">
                                                                    Add the creator&apos;s mobile number first to link their profile and continue with collaboration.
                                                                </p>
                                                                <button
                                                                    type="button"
                                                                    className="theme-btn btn-primary"
                                                                    onClick={handleAddCreatorMobile}
                                                                >
                                                                    Add Creator Mobile
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <>
                                                        <div className={`${isDesktop ? 'w-50' : 'w-100'} h-100 ${showCollabSection ? '' : 'd-none'}`}>
                                                            <SimpleBar style={{ height: "100%" }}>
                                                                <div className={`tab-controls milestone-tab-controls`} ref={tabsRef}>
                                                                    {tabs.map(tab => (
                                                                        <a
                                                                            key={tab.key}
                                                                            onClick={() => {
                                                                                setActiveTab(tab.key)
                                                                            }}
                                                                            className={`milestone-tab d-flex align-items-center cursor-pointer justify-content-center gap-2 ${activeTab === tab.key ? 'active-tab' : ''}`}
                                                                        >
                                                                            {tab.label}
                                                                        </a>
                                                                    ))}
                                                                </div>
                                                                {(deletionRequest) && (
                                                                    <div className={`collab-deletion-request-banner collab-deletion-request-banner--${(deletionRequest?.status || 'pending').toLowerCase()}`}>
                                                                        <div className="d-flex align-items-center justify-content-between gap-2">
                                                                            <span className="collab-deletion-request-title">Your collab removal request is {(deletionRequest?.status ?? 'pending').replace(/_/g, ' ')}.</span>
                                                                        </div>
                                                                        {(deletionRequest?.remark) && (
                                                                            <p className="collab-deletion-request-remark">{deletionRequest?.remark}</p>
                                                                        )}
                                                                    </div>
                                                                )}
                                                                <div className="milestone-tab-content">
                                                                    <div className={`collapse ${activeTab === 'timeline' && 'show'}`}>
                                                                        {
                                                                            // activeMilestone &&
                                                                            <Timeline
                                                                                onFeedbackView={(fb) => setFeedback(fb)}
                                                                                active_steps={currentTasks}
                                                                                events={events}
                                                                                setOpenedStep={setOpenedStep}
                                                                                milestone={activeMilestone}
                                                                                addDeadline={(step) => setDeadlineCalendarStep(step)}
                                                                                onPlayBtnClick={onPlayButtonClick}
                                                                                setSelectedMedia={setSelectedMedia}
                                                                                onStepProcess={() => fetchCollaborationMilestones(true)}
                                                                            />
                                                                        }

                                                                    </div>
                                                                    <div className={`collapse ${activeTab === 'specs' && 'show'}`}>
                                                                        {
                                                                            activeMilestone && campaign &&
                                                                            <Specifications
                                                                                campaign={campaign}
                                                                                activeMilestone={activeMilestone}
                                                                                acknowledgementRequired={false}
                                                                                onAcknowledgement={() => fetchCollaborationMilestones(true)}
                                                                                showDisabledTooltip={showDisabledTooltip}
                                                                                setSelectedMedia={setSelectedMedia}
                                                                            />
                                                                        }
                                                                    </div>
                                                                    <div className={`collapse ${activeTab === 'content' && 'show'}`}>
                                                                        <CollaborationContent collaborationPosts={posts} />
                                                                    </div>
                                                                </div>
                                                                {
                                                                    getCurrentStepsComponents.map((component) => component)
                                                                }
                                                                <FeedbackModal
                                                                    isActive={!!feedback}
                                                                    onClose={() => setFeedback('')}
                                                                    feedback={feedback}
                                                                />
                                                                <DeadlineCalendarModal
                                                                    isActive={!!deadlineCalendarStep}
                                                                    onClose={(refetch) => {
                                                                        setDeadlineCalendarStep(null)
                                                                        if (refetch) {
                                                                            fetchCollaborationMilestones(true);
                                                                        }
                                                                    }}
                                                                    step={deadlineCalendarStep!}
                                                                />
                                                                <InfluencerPostModal
                                                                    isOpen={!!influencerPostModalVisibile}
                                                                    onClose={(processed) => {
                                                                        if (processed) fetchCollaborationMilestones(true);
                                                                    }}
                                                                    postId={influencerPostModalVisibile?.postId!}
                                                                    influencer={influencerPostModalVisibile?.influencer!}
                                                                    status={influencerPostModalVisibile?.status}
                                                                    milestoneStep={influencerPostModalVisibile?.milestoneStep}
                                                                    socialAccount={influencerPostModalVisibile?.socialAccount!}
                                                                />
                                                                <Modal centered isOpen={!!selectedMedia} toggle={() => setSelectedMedia(null)}>
                                                                    {/* <CloseButton top='0' onClose={() => setSelectedMedia(null)} /> */}
                                                                    <ModalBody className='p-0 overflow-hidden' style={{ borderRadius: '6px', height: '75vh' }}>
                                                                        {
                                                                            selectedMedia &&
                                                                            <div className='w-100 h-100 d-flex justify-content-center align-items-center'>
                                                                                {
                                                                                    getMediaTypeFromURL(selectedMedia.url) === 'video' &&
                                                                                    <video src={selectedMedia.url} autoPlay controls className='h-100 w-100 bgc-content' />
                                                                                }
                                                                                {
                                                                                    getMediaTypeFromURL(selectedMedia.url) === 'image' &&
                                                                                    <div className="h-100 w-100 bg-media bgc-content" style={{
                                                                                        backgroundImage: `url(${selectedMedia.url})`
                                                                                    }}></div>
                                                                                }
                                                                                <div className="media-action">
                                                                                    {
                                                                                        selectedMedia.gig &&
                                                                                        <span className='cursor-pointer text-white fs-5' onClick={() => handleDownload(selectedMedia.gig)}><i className="fa-solid fa-download"></i></span>
                                                                                    }
                                                                                    <span className='text-white fs-5' onClick={() => setSelectedMedia(null)}><i className="fa-solid fa-circle-xmark"></i></span>
                                                                                </div>
                                                                            </div>
                                                                        }
                                                                    </ModalBody>
                                                                </Modal>
                                                            </SimpleBar>
                                                        </div>
                                                        <div className={`${isDesktop ? 'w-50' : 'w-100'} h-100 collab-chat-section ${showChatSection ? '' : 'd-none'}`}>
                                                            {
                                                                chatRoomId ? (
                                                                    <CollaborationChat activeRoomId={chatRoomId} />
                                                                ) : (
                                                                    <div className='h-100'>
                                                                        <div className='d-flex flex-column justify-content-center align-items-center h-100'>
                                                                            {
                                                                                collabLoading ? (
                                                                                    <>
                                                                                        <Spinner />
                                                                                        <div>Loading Chat...</div>
                                                                                    </>
                                                                                ) : (
                                                                                    <>
                                                                                        <h5 className='mb-3'>You can chat with creator</h5>
                                                                                        <button className='btn theme-btn-primary' disabled={creatingChatRoom} onClick={createChatRoom}>{creatingChatRoom ? 'Creating Chat Room...' : 'Start Chat'}</button>
                                                                                    </>
                                                                                )
                                                                            }
                                                                        </div>
                                                                    </div>
                                                                )
                                                            }
                                                        </div>
                                                    </>
                                                )}
                                            </>
                                        )
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            }
        </CollaborationLayout>
    )
}

export default TaskMilestone